// Windows and doors: where the openings go in the walls the rooms already have.
//
// A window goes in every stretch of facade a habitable room owns, sized by
// the room; the living room opens onto its balcony through a glazed door.
// Inside, each flat's rooms are joined by doors into a tree grown from the
// front door: from the hall where it can, from the living room otherwise,
// which is the order an architect reaches for. A room nothing can be
// reached from is a fault, and the tests look for one.
import { distanceTo, distanceToOutline, type MultiPoly, type Pt } from "./geom.ts";
import type { Room, RoomKind, Unit } from "./layout.ts";
import { ENTRY, PASS_THROUGH } from "./rooms.ts";

export type OpeningKind = "window" | "balconyDoor" | "frontDoor" | "door" | "entrance" | "shopfront";

export interface Opening {
  kind: OpeningKind;
  a: Pt;
  b: Pt;
  /** For a door: the rooms it joins, by index into the flat's rooms (-1: outside the flat). */
  joins?: [number, number];
}

const WINDOW_WIDTH: Partial<Record<RoomKind, number>> = { living: 2.4, bedroom: 1.5, kitchen: 1.2 };
const DOOR = 0.8;
const FRONT_DOOR = 1.0;
const ENTRANCE = 1.8;
/** How close to the facade a room edge must be to count as an outside wall. */
const ON = 0.05;

type Seg = [Pt, Pt];

const len = ([a, b]: Seg) => Math.hypot(b[0] - a[0], b[1] - a[1]);
const lerp = (a: Pt, b: Pt, t: number): Pt => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];

/** A centred piece of a segment, `w` long (or all of it, less margins, if shorter). */
function centred([a, b]: Seg, w: number, margin = 0.3): Seg | undefined {
  const L = len([a, b]);
  const width = Math.min(w, L - 2 * margin);
  if (width < 0.6) return undefined;
  const t0 = (L - width) / 2 / L;
  return [lerp(a, b, t0), lerp(a, b, 1 - t0)];
}

function edgesOf(mp: MultiPoly): Seg[] {
  const out: Seg[] = [];
  for (const poly of mp) for (const ring of poly) ring.forEach((p, i) => out.push([p, ring[(i + 1) % ring.length]]));
  return out;
}

/** Edges of a room that lie on the outline of the storey: its outside walls. */
export function facadeEdges(room: MultiPoly, storey: MultiPoly): Seg[] {
  return edgesOf(room).filter(([a, b]) => {
    const m = lerp(a, b, 0.5);
    return len([a, b]) >= 0.9 && distanceToOutline(a, storey) < ON && distanceToOutline(b, storey) < ON && distanceToOutline(m, storey) < ON;
  });
}

/** The longest stretch of wall two rooms share, if any, at least `min` long. */
/**
 * The longest stretch of wall two rooms share. Collinear pieces are merged
 * first on both sides: a vertex left on an outline by an earlier cut splits
 * one wall into two edges, and measured edge by edge a 1.2 m wall became
 * 0.72 + 0.48 m, too short for a door either way — which is how living
 * rooms ended up with no way in.
 */
function sharedWall(A: MultiPoly, B: MultiPoly, min = DOOR + 0.2): Seg | undefined {
  let best: Seg | undefined, bestL = 0;
  const ea = edgesOf(A), eb = edgesOf(B);
  const merge = (iv: [number, number][]) => {
    iv.sort((x, y) => x[0] - y[0]);
    const out: [number, number][] = [];
    for (const [lo, hi] of iv) { const last = out[out.length - 1]; if (last && lo <= last[1] + 0.02) last[1] = Math.max(last[1], hi); else out.push([lo, hi]); }
    return out;
  };
  for (const [a, b] of ea) {
    const L = len([a, b]);
    if (L < 1e-6) continue;
    const ux = (b[0] - a[0]) / L, uy = (b[1] - a[1]) / L;
    const off = (p: Pt) => Math.abs((p[0] - a[0]) * uy - (p[1] - a[1]) * ux);
    const t = (p: Pt) => (p[0] - a[0]) * ux + (p[1] - a[1]) * uy;
    const on = (es: Seg[]) => merge(es.filter(([c, d]) => off(c) <= 0.02 && off(d) <= 0.02).map(([c, d]) => [Math.min(t(c), t(d)), Math.max(t(c), t(d))] as [number, number]));
    const ia = on(ea), ib = on(eb);
    for (const [a0, a1] of ia) for (const [b0, b1] of ib) {
      const lo = Math.max(a0, b0), hi = Math.min(a1, b1);
      if (hi - lo > bestL) { bestL = hi - lo; best = [[a[0] + ux * lo, a[1] + uy * lo], [a[0] + ux * hi, a[1] + uy * hi]]; }
    }
  }
  return bestL >= min ? best : undefined;
}

/** Order in which a room is reached from: the hall first, then the living room, then anything. */
const VIA: RoomKind[] = ["hall", "living", "kitchen", "storage", "bedroom", "bath"];

export function flatOpenings(u: Unit, storey: MultiPoly): Opening[] {
  const rooms = u.rooms ?? [];
  const out: Opening[] = [];

  // windows, and the balcony door off the living room
  rooms.forEach((r) => {
    const w = WINDOW_WIDTH[r.kind];
    if (!w) return;
    for (const e of facadeEdges(r.polygon, storey)) {
      // where the balcony runs along this wall, the opening is a glazed door onto it
      const along = u.balcony && r.kind !== "kitchen"
        ? [0.15, 0.3, 0.5, 0.7, 0.85].filter((t) => distanceTo(lerp(e[0], e[1], t), u.balcony!) < 0.3)
        : [];
      if (along.length) {
        const part: Seg = [lerp(e[0], e[1], Math.min(...along) - 0.1), lerp(e[0], e[1], Math.max(...along) + 0.1)];
        const seg = centred(part, 2.0, 0.1);
        if (seg) { out.push({ kind: "balconyDoor", a: seg[0], b: seg[1] }); continue; }
      }
      const seg = centred(e, w);
      if (seg) out.push({ kind: "window", a: seg[0], b: seg[1] });
    }
  });

  // the front door, on the flat's own outline where the door point is
  let entry = -1;
  if (u.door) {
    const wall = edgesOf(u.polygon).find(([a, b]) => distanceToOutline(u.door!, [[[a, b, a]]]) < 0.2);
    if (wall) {
      const L = len(wall), t = Math.max(0, Math.min(1, ((u.door[0] - wall[0][0]) * (wall[1][0] - wall[0][0]) + (u.door[1] - wall[0][1]) * (wall[1][1] - wall[0][1])) / (L * L)));
      const h = Math.min(FRONT_DOOR / 2, L / 2) / L;
      out.push({ kind: "frontDoor", a: lerp(wall[0], wall[1], Math.max(0, t - h)), b: lerp(wall[0], wall[1], Math.min(1, t + h)) });
    }
    entry = entryRoom(rooms, u.door);
  }

  // interior doors: a tree from the entry room, preferring the hall, then the
  // living room; a room reached only through a short wall gets a narrow door
  if (entry >= 0) {
    const reached = new Set([entry]);
    for (const [minWall, width] of [[DOOR + 0.2, DOOR], [0.75, 0.7]] as const) for (let grew = true; grew;) {
      grew = false;
      for (let i = 0; i < rooms.length; i++) {
        if (reached.has(i)) continue;
        // only through the rooms the rules allow for this one, the preferred first
        const allowed = PASS_THROUGH[rooms[i].kind];
        const from = [...reached].filter((x) => allowed.includes(rooms[x].kind)).sort((x, y) => allowed.indexOf(rooms[x].kind) - allowed.indexOf(rooms[y].kind));
        for (const j of from) {
          const wall = sharedWall(rooms[i].polygon, rooms[j].polygon, minWall);
          if (!wall) continue;
          const seg = centred(wall, width, 0.025);
          if (!seg) continue;
          out.push({ kind: "door", a: seg[0], b: seg[1], joins: [j, i] });
          reached.add(i);
          grew = true;
          break;
        }
      }
    }
  }
  return out;
}

/** Shopfronts on a ground-floor shop's facade, nearly the whole width. */
export function shopOpenings(u: Unit, storey: MultiPoly): Opening[] {
  return facadeEdges(u.polygon, storey)
    .map((e) => centred(e, len(e) - 0.6, 0.3))
    .filter((s): s is Seg => !!s)
    .map((s) => ({ kind: "shopfront" as const, a: s[0], b: s[1] }));
}

/** The building entrance of a ground-floor core, on the facade at its entrance point. */
export function entranceOpening(u: Unit, storey: MultiPoly): Opening[] {
  if (!u.entrance) return [];
  const wall = facadeEdges(u.polygon, storey).find(([a, b]) => distanceToOutline(u.entrance!, [[[a, b, a]]]) < 0.3);
  if (!wall) return [];
  const seg = centred(wall, ENTRANCE, 0.2);
  return seg ? [{ kind: "entrance", a: seg[0], b: seg[1] }] : [];
}

/**
 * The room the front door opens into: of the rooms whose wall it is in, the
 * hall first, then the living room — a door that also touches a storage
 * room does not open into the storage room.
 */
export function entryRoom(rooms: Room[], door: Pt): number {
  const rank = (k: RoomKind) => (ENTRY.includes(k) ? ENTRY.indexOf(k) : ENTRY.length + VIA.indexOf(k));
  const near = rooms.map((r, i) => ({ i, d: distanceToOutline(door, r.polygon), k: rank(r.kind) })).filter((x) => x.d < 0.3);
  if (near.length) return near.sort((a, b) => a.k - b.k || a.d - b.d)[0].i;
  let best = -1, bestD = Infinity;
  rooms.forEach((r, i) => { const d = distanceToOutline(door, r.polygon); if (d < bestD) { bestD = d; best = i; } });
  return best;
}

/** Every room of a flat reachable from its front door, by index. */
export function reachableRooms(u: Unit): Set<number> {
  const rooms: Room[] = u.rooms ?? [];
  const doors = (u.openings ?? []).filter((o) => o.kind === "door" && o.joins);
  const entry = u.door ? entryRoom(rooms, u.door) : -1;
  const seen = new Set<number>(entry >= 0 ? [entry] : []);
  for (let grew = true; grew;) {
    grew = false;
    for (const d of doors) for (const [x, y] of [d.joins!, [d.joins![1], d.joins![0]] as [number, number]]) {
      if (seen.has(x) && !seen.has(y)) { seen.add(y); grew = true; }
    }
  }
  return seen;
}
